import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "./useNotifications";
import { useContactMessagesBadge } from "../../hooks/useContactMessagesBadge";

// ─── Icons ────────────────────────────────────────────────────────────────────
const Icon = ({ d, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);
const BellIcon   = () => <Icon d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />;
const MailIcon   = () => <Icon d="M4 4h16v16H4V4zm0 0l8 8 8-8" />;
const SearchIcon = () => <Icon d="M21 21l-6-6m2-5a7 7 0 1 1-14 0 7 7 0 0 1 14 0" />;
const MenuIcon   = () => <Icon d="M3 12h18M3 6h18M3 18h18" />;

// ─── Header ───────────────────────────────────────────────────────────────────
export function Header({ onMenuClick }) {
  const user    = JSON.parse(localStorage.getItem("adminUser") || "{}");
  const initial = (user.fullName || user.name || "A").charAt(0).toUpperCase();

  const navigate = useNavigate();
  const { notifications, unreadCount, ringing, markRead, markAllRead } =
    useNotifications({ pollInterval: 30000 });

  const { unreadCount: unreadMessages, recent: recentMessages, refresh: refreshMessages } =
    useContactMessagesBadge();

  const [panelOpen, setPanelOpen]   = useState(false);
  const [mailPanelOpen, setMailPanelOpen] = useState(false);
  const bellWrapRef = useRef(null);
  const mailWrapRef = useRef(null);

  // close notification panel when clicking outside
  useEffect(() => {
    if (!panelOpen) return;
    const handle = (e) => {
      if (bellWrapRef.current && !bellWrapRef.current.contains(e.target)) {
        setPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [panelOpen]);

  // close mail panel when clicking outside
  useEffect(() => {
    if (!mailPanelOpen) return;
    const handle = (e) => {
      if (mailWrapRef.current && !mailWrapRef.current.contains(e.target)) {
        setMailPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [mailPanelOpen]);

  function handleNotificationClick(n) {
    markRead(n.id);
    setPanelOpen(false);
    // navigates to /applications/:id  — change the path to match your route
    navigate(`/applications/${n.applicationId}`);
  }

  async function handleMessageClick(m) {
    setMailPanelOpen(false);
    navigate("/dashboard/contact-mesaaging");
    refreshMessages();
  }

  return (
    <header className="admin_header">
      <button className="admin_header__menu-btn" onClick={onMenuClick}>
        <MenuIcon />
      </button>

      <div className="admin_header__search">
        <span className="admin_header__search-icon">
          <SearchIcon />
        </span>
        <input placeholder="Search..." />
      </div>

      <div className="admin_header__spacer" />

      {/* ── Contact Messages Bell ── */}
      <div ref={mailWrapRef} style={{ position: "relative" }}>
        <button
          className="admin_header__bell-btn"
          onClick={() => setMailPanelOpen((o) => !o)}
          title="Contact messages"
        >
          <MailIcon />
          {unreadMessages > 0 && (
            <span className="admin_header__bell-badge">
              {unreadMessages > 9 ? "9+" : unreadMessages}
            </span>
          )}
        </button>

        {mailPanelOpen && (
          <div className="notif-panel">
            <div className="notif-panel__header">
              <span>Contact Messages</span>
            </div>

            <div className="notif-panel__list">
              {recentMessages.length === 0 ? (
                <p className="notif-panel__empty">No messages yet</p>
              ) : (
                recentMessages.map((m) => (
                  <div
                    key={m._id}
                    className={`notif-item ${m.isRead ? "" : "notif-item--unread"}`}
                    onClick={() => handleMessageClick(m)}
                  >
                    <div className="notif-item__body">
                      <p className="notif-item__title">{m.name}</p>
                      <p className="notif-item__msg">{m.message}</p>
                      <span className="notif-item__time">{timeAgo(new Date(m.createdAt).getTime())}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Applications Bell ── */}
      <div ref={bellWrapRef} style={{ position: "relative" }}>
        <button
          className={`admin_header__bell-btn ${ringing ? "admin_header__bell-btn--ringing" : ""}`}
          onClick={() => setPanelOpen((o) => !o)}
        >
          <BellIcon />
          {unreadCount > 0 && (
            <span className="admin_header__bell-badge">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>

        {/* ── Dropdown panel ── */}
        {panelOpen && (
          <div className="notif-panel">
            <div className="notif-panel__header">
              <span>Notifications</span>
              <button className="notif-panel__mark-all" onClick={markAllRead}>
                Mark all as read
              </button>
            </div>

            <div className="notif-panel__list">
              {notifications.length === 0 ? (
                <p className="notif-panel__empty">No notifications yet</p>
              ) : (
                [...notifications].reverse().map((n) => (
                  <div
                    key={n.id}
                    className={`notif-item ${n.read ? "" : "notif-item--unread"}`}
                    onClick={() => handleNotificationClick(n)}
                  >
                    <div className="notif-item__body">
                      <p className="notif-item__title">{n.title}</p>
                      <p className="notif-item__msg">{n.msg}</p>
                      <span className="notif-item__time">{timeAgo(n.time)}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <div className="admin_header__avatar">{initial}</div>
    </header>
  );
}

// ─── Helper ───────────────────────────────────────────────────────────────────
function timeAgo(ts) {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}